/**
 * Support Guide: Score a Rider as a Judge
 * Category: Scoring (yellow #eab308)
 *
 * 360 frames @ 30fps = 12 seconds
 *
 * Timeline:
 *   0-45    Title card
 *   45-360  Continuous walkthrough on the judge screen
 *
 * Demonstrates the current judge UI:
 *   - Attempt boxes with colour coding (yellow = needs score, green = current)
 *   - Tap attempt → whole-number picker → decimal sub-grid
 *   - Score submits instantly, auto-advances
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { TypeWriter } from "../../components/TypeWriter";
import { AutoCursor } from "../../components/AutoCursor";
import { ScreenMock } from "../../components/ScreenMock";

const ACCENT = "#eab308";
const GREEN = "#22c55e";
const WARNING = "#eab308";

// ─── Rider data ─────────────────────────────────────────────────
const RIDERS = [
  { name: "Harley Clifford", nick: "The King" },
  { name: "Nic Rapa", nick: "Raptor" },
  { name: "Dallas Friday", nick: "D-Fri" },
  { name: "Amber Wing", nick: "Wings" },
  { name: "Steel Lafferty", nick: "Steele" },
];

const MAX_ATTEMPTS = 3;

// ─── Main ────────────────────────────────────────────────────────
export function ScoreRiderGuide() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={45}>
        <TitleCard />
      </Sequence>
      <Sequence from={45} durationInFrames={315}>
        <Walkthrough />
      </Sequence>
    </AbsoluteFill>
  );
}

function TitleCard() {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 25], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 10, color: ACCENT, textTransform: "uppercase", letterSpacing: 3, fontFamily: "'JetBrains Mono', monospace", marginBottom: 16 }}>Support Guide</div>
        <div style={{ fontSize: 52, fontWeight: 800, color: "#fff", maxWidth: 800, lineHeight: 1.1 }}>Score a Rider as a Judge</div>
        <div style={{ marginTop: 20 }}><TypeWriter text="6 steps · Scoring" delay={10} speed={1} style={{ fontSize: 16, color: "#94a3b8" }} /></div>
      </div>
    </AbsoluteFill>
  );
}

// ─── Walkthrough ─────────────────────────────────────────────────
function Walkthrough() {
  const frame = useCurrentFrame();

  // Heat: switches to Finals at f30
  const isFinals = frame >= 30;

  // Rider 0 (Harley) state:
  //   current_attempt starts at 2 (attempt 1 already scored by producer advancing)
  //   Attempt 1: already has score 7.5
  //   Attempt 2: needs scoring (yellow) — scored at f95 with 8.5
  //   Attempt 3: future until producer advances
  const r0CurrentAttempt = 2;
  const r0Attempt1Score = 7.5;
  let r0Attempt2Score: number | null = null;
  if (frame >= 95) r0Attempt2Score = 8.5;

  // Rider 0 scoring mode phases:
  //   f70-75: cursor moves to attempt 2 box
  //   f80: clicks attempt 2 → enters scoring mode (whole numbers)
  //   f85: cursor moves to "8"
  //   f88: clicks "8" → decimal sub-grid appears
  //   f92: cursor moves to "8.5"
  //   f95: clicks "8.5" → score submitted, back to attempt view
  let r0ScoringMode: "attempts" | "whole" | "decimal" = "attempts";
  if (frame >= 80 && frame < 88) r0ScoringMode = "whole";
  if (frame >= 88 && frame < 95) r0ScoringMode = "decimal";
  // After 95, score is submitted, back to attempts

  // Rider 1 (Nic) state:
  //   current_attempt = 1 (green + yellow = needs scoring, is current)
  //   Scored at f200 with 7.0
  let r1Attempt1Score: number | null = null;
  if (frame >= 200) r1Attempt1Score = 7.0;

  let r1ScoringMode: "attempts" | "whole" = "attempts";
  if (frame >= 185 && frame < 195) r1ScoringMode = "whole";

  // Flash for real-time submission
  const showFlash = (frame >= 95 && frame < 115) || (frame >= 200 && frame < 220);

  // Summary
  const showSummary = frame >= 265;
  const summaryOp = interpolate(frame, [265, 285], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const screenOp = interpolate(frame, [0, 15], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // Step labels
  const steps: Array<{ num: number; text: string; show: number; hide: number }> = [
    { num: 1, text: "Open Judge screen", show: 0, hide: 25 },
    { num: 2, text: "Select heat", show: 15, hide: 55 },
    { num: 3, text: "Yellow = needs your score", show: 55, hide: 90 },
    { num: 4, text: "Tap attempt → pick score", show: 75, hide: 120 },
    { num: 5, text: "Tap decimal for precision", show: 88, hide: 130 },
    { num: 6, text: "Score submits instantly", show: 95, hide: 145 },
  ];

  return (
    <AbsoluteFill style={{ opacity: screenOp }}>
      {steps.map((s) => (
        <StepBadge key={s.num} frame={frame} start={s.show} end={s.hide} num={s.num} text={s.text} />
      ))}

      <ScreenMock url="raley.koda-software.com/competition/boardstock/judge">
        <JudgeMock
          selectedHeat={isFinals ? 1 : 0}
          riders={RIDERS}
          maxAttempts={MAX_ATTEMPTS}
          riderStates={[
            {
              currentAttempt: r0CurrentAttempt,
              scores: { 1: r0Attempt1Score, 2: r0Attempt2Score },
              scoringMode: r0ScoringMode,
              decimalBase: r0ScoringMode === "decimal" ? 8 : null,
            },
            {
              currentAttempt: 1,
              scores: { 1: r1Attempt1Score },
              scoringMode: r1ScoringMode,
              decimalBase: null,
            },
            { currentAttempt: 1, scores: {}, scoringMode: "attempts", decimalBase: null },
            { currentAttempt: 1, scores: {}, scoringMode: "attempts", decimalBase: null },
            { currentAttempt: 1, scores: {}, scoringMode: "attempts", decimalBase: null },
          ]}
          showFlash={showFlash}
          frame={frame}
        />
        <AutoCursor points={[
          { x: 960, y: 540, frame: 0 },
          { target: "heat-finals-tab", frame: 20 },
          { target: "heat-finals-tab", frame: 30, click: true },
          // Point at yellow attempt 2 box for Harley
          { target: "r0-attempt-2", frame: 70 },
          { target: "r0-attempt-2", frame: 80, click: true },
          // Score picker: click "8"
          { target: "r0-score-8", frame: 85 },
          { target: "r0-score-8", frame: 88, click: true },
          // Decimal sub-grid: click "8.5"
          { target: "r0-dec-85", frame: 92 },
          { target: "r0-dec-85", frame: 95, click: true },
          // Score Nic — click attempt 1
          { target: "r1-attempt-1", frame: 170 },
          { target: "r1-attempt-1", frame: 180, click: true },
          { target: "r1-score-7", frame: 190 },
          { target: "r1-score-7", frame: 195, click: true },
          // After scoring, cursor idles
          { x: 960, y: 540, frame: 245 },
          { x: 960, y: 540, frame: 315 },
        ]} />
      </ScreenMock>

      {/* Colour legend overlay */}
      {frame >= 55 && frame < 95 && (
        <div style={{
          position: "absolute", bottom: 60, left: 0, right: 0,
          display: "flex", justifyContent: "center", gap: 32,
          opacity: interpolate(frame, [55, 65, 85, 95], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
          zIndex: 100,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 16, height: 16, border: `2px solid ${WARNING}`, background: `${WARNING}22`, borderRadius: 2 }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: WARNING }}>Needs your score</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 16, height: 16, border: `2px solid ${GREEN}`, borderRadius: 2, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: GREEN }} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: GREEN }}>Current attempt</span>
          </div>
        </div>
      )}

      {/* Summary overlay */}
      {showSummary && (
        <div style={{
          position: "absolute", bottom: 60, left: 0, right: 0,
          display: "flex", justifyContent: "center", opacity: summaryOp, zIndex: 100,
        }}>
          <div style={{
            background: "rgba(10,10,11,0.92)", border: `1px solid ${ACCENT}`,
            padding: "16px 40px", textAlign: "center",
          }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: GREEN }}>You're ready to judge!</div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 6, fontFamily: "'JetBrains Mono', monospace" }}>
              Yellow = needs score · Green = current · Tap → pick → done
            </div>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
}

// ─── Step Badge ──────────────────────────────────────────────────
function StepBadge({ frame, start, end, num, text }: {
  frame: number; start: number; end: number; num: number; text: string;
}) {
  if (frame < start || frame > end) return null;
  const fadeIn = interpolate(frame, [start, start + 10], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const fadeOut = interpolate(frame, [end - 10, end], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{
      position: "absolute", top: 20, left: 20, zIndex: 200,
      opacity: Math.min(fadeIn, fadeOut), display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{
        background: ACCENT, color: "#000", fontSize: 10, fontWeight: 800,
        padding: "3px 8px", fontFamily: "'JetBrains Mono', monospace",
        textTransform: "uppercase", letterSpacing: 1,
      }}>Step {num}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{text}</div>
    </div>
  );
}

// ─── Judge Screen Mock (matches current Judge.tsx UI) ────────────
interface RiderState {
  currentAttempt: number;
  scores: Record<number, number | null>;
  scoringMode: "attempts" | "whole" | "decimal";
  decimalBase: number | null;
}

function JudgeMock({ selectedHeat, riders, maxAttempts, riderStates, showFlash, frame }: {
  selectedHeat: number;
  riders: { name: string; nick: string }[];
  maxAttempts: number;
  riderStates: RiderState[];
  showFlash: boolean;
  frame: number;
}) {
  const pulse = showFlash ? interpolate(frame % 15, [0, 7, 14], [0.4, 1, 0.4]) : 0;

  return (
    <div style={shell}>
      <div style={topbar}>
        <span style={brandStyle}>RALEY</span>
        <span style={{ marginLeft: "auto", ...mono, fontSize: 11, color: "#71717a" }}>judge1@example.com</span>
      </div>
      <div style={{ padding: "16px 20px", flex: 1 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #2a2a32", paddingBottom: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Judge: Britta</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
              <span style={{ fontSize: 11, color: "#71717a" }}>Boardstock Invitational · average scoring</span>
              {showFlash && (
                <span style={{ fontSize: 9, fontWeight: 700, color: GREEN, textTransform: "uppercase", ...mono, letterSpacing: 1, opacity: pulse }}>LIVE</span>
              )}
            </div>
          </div>
        </div>

        {/* Heat tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {["Round 1", "Finals — Pro"].map((h, i) => (
            <div key={h} id={i === 1 ? "heat-finals-tab" : undefined} style={{
              padding: "4px 14px", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
              background: i === selectedHeat ? "#3b82f6" : "#1a1a1e",
              color: i === selectedHeat ? "#fff" : "#71717a",
              border: `1px solid ${i === selectedHeat ? "#3b82f6" : "#2a2a32"}`,
            }}>{h}</div>
          ))}
        </div>

        {/* Column headers */}
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 40px", gap: 8, padding: "0 8px", marginBottom: 6 }}>
          <span style={colLabel}>Rider</span>
          <span style={colLabel}>Attempts / Score</span>
          <span />
        </div>

        {/* Rider rows */}
        {riders.map((r, i) => {
          const state = riderStates[i];
          const hasAnyScore = Object.values(state.scores).some((v) => v != null);

          return (
            <div key={r.name} style={{
              display: "grid", gridTemplateColumns: "160px 1fr 40px", gap: 8,
              padding: "6px 8px", alignItems: "center",
              background: hasAnyScore ? "var(--c-surface, #111113)" : "transparent",
              border: "1px solid #1e1e24",
              marginBottom: 3,
            }}>
              {/* Name */}
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>{r.name}</div>
                <div style={{ fontSize: 10, color: "#52525b" }}>{r.nick}</div>
              </div>

              {/* Score area */}
              <div style={{ display: "flex", gap: 2 }}>
                {state.scoringMode === "attempts" ? (
                  /* Attempt boxes */
                  Array.from({ length: maxAttempts }, (_, a) => {
                    const attemptNum = a + 1;
                    const score = state.scores[attemptNum] ?? null;
                    const hasScore = score !== null;
                    const isLive = attemptNum === state.currentAttempt;
                    const isAvailable = attemptNum <= state.currentAttempt;
                    const isFuture = attemptNum > state.currentAttempt;
                    const needsScore = isAvailable && !hasScore;

                    return (
                      <div
                        key={attemptNum}
                        id={i === 0 && attemptNum === 2 ? "r0-attempt-2" : i === 1 && attemptNum === 1 ? "r1-attempt-1" : undefined}
                        style={{
                          flex: "1 1 0",
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          fontSize: hasScore ? 11 : 9,
                          fontWeight: hasScore ? 700 : 500,
                          ...mono,
                          background: hasScore ? "#1a1a1e" : needsScore ? `${WARNING}1a` : "transparent",
                          color: hasScore ? "#e4e4e7" : needsScore ? WARNING : isFuture ? "#2a2a32" : "#52525b",
                          border: `1px solid ${needsScore ? WARNING : isLive ? GREEN : "#2a2a32"}`,
                          boxShadow: isLive && !needsScore ? `0 0 0 1px ${GREEN}` : "none",
                          opacity: isFuture ? 0.3 : 1,
                        }}
                      >
                        {hasScore ? score.toFixed(1) : `#${attemptNum}`}
                        {needsScore && (
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: WARNING, flexShrink: 0 }} />
                        )}
                        {isLive && hasScore && (
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: GREEN, flexShrink: 0 }} />
                        )}
                      </div>
                    );
                  })
                ) : state.scoringMode === "decimal" && state.decimalBase != null ? (
                  /* Decimal sub-grid */
                  <>
                    <div style={{ flex: "0 0 24px", height: 32, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, background: "#1a1a1e", color: "#71717a", border: "1px solid #2a2a32" }}>
                      &larr;
                    </div>
                    {Array.from({ length: 10 }, (_, d) => {
                      const val = state.decimalBase! + d * 0.1;
                      const rounded = Math.round(val * 10) / 10;
                      return (
                        <div
                          key={rounded}
                          id={i === 0 && rounded === 8.5 ? "r0-dec-85" : undefined}
                          style={{
                            flex: "1 1 0", height: 32,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 10, fontWeight: 500, ...mono,
                            background: "#1a1a1e", color: "#71717a",
                            border: "1px solid transparent",
                          }}
                        >
                          {rounded.toFixed(1)}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  /* Whole-number score picker */
                  <>
                    {maxAttempts > 1 && (
                      <div style={{ flex: "0 0 24px", height: 32, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, background: "#1a1a1e", color: "#71717a", border: "1px solid #2a2a32" }}>
                        &larr;
                      </div>
                    )}
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => (
                      <div
                        key={v}
                        id={i === 0 && v === 8 ? "r0-score-8" : i === 1 && v === 7 ? "r1-score-7" : undefined}
                        style={{
                          flex: "1 1 0", height: 32,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 500, ...mono,
                          background: "#1a1a1e", color: "#52525b",
                          border: "1px solid transparent",
                        }}
                      >
                        {v}
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Status */}
              <div style={{ fontSize: 9, color: "#3f3f46", textAlign: "center" }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────
const shell: React.CSSProperties = { width: "100%", height: "100%", background: "#0a0a0b", fontFamily: "'Inter', sans-serif", color: "#e4e4e7", display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 40, background: "#111113", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", gap: 24, padding: "0 20px", flexShrink: 0 };
const brandStyle: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 14, letterSpacing: 2 };
const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };
const colLabel: React.CSSProperties = { fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#52525b", fontFamily: "'JetBrains Mono', monospace" };
